import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDownIcon, ChevronRightIcon, XMarkIcon } from "@heroicons/react/24/outline";

const isActive = (pathname, target) =>
    pathname === target || pathname.startsWith(`${target}/`);

export function GeneralMobileHeader(props) {
    const [menuList, setMenuList] = useState([]);
    const [expandedMenus, setExpandedMenus] = useState(new Set());
    const location = useLocation();

    useEffect(() => {
        if (props.permissionData.length > 0) {
            const menu_key = "menu_name";
            const menu_list = [
                ...new Map(props.permissionData.map(menu =>
                    [menu[menu_key], menu])).values()
            ];
            setMenuList(menu_list);

            // Expand menus that contain the active route
            const activeMenus = new Set();
            menu_list.forEach(menu => {
                const sub_menu_list = props.permissionData.filter(p => p.menu_id === menu.menu_id);
                const hasActiveItem = sub_menu_list.some(sub => 
                    isActive(location.pathname, sub.sub_menu_link)
                );
                if (hasActiveItem) {
                    activeMenus.add(menu.menu_id);
                }
            });
            setExpandedMenus(activeMenus);
        }
    }, [props.permissionData, location.pathname]);

    const toggleMenu = (menuId) => {
        setExpandedMenus(prev => {
            const newSet = new Set(prev);
            if (newSet.has(menuId)) {
                newSet.delete(menuId);
            } else {
                newSet.add(menuId);
            }
            return newSet;
        });
    };

    return (
        <div className="flex h-full w-80 flex-col bg-slate-900 text-slate-100">
            {/* Mobile Header */}
            <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-indigo-600 flex items-center justify-center text-sm font-bold">
                        HR
                    </div>
                    <div>
                        <p className="text-sm uppercase tracking-wide text-slate-400">Payroll Suite</p>
                        <p className="text-lg font-semibold text-white">Control Center</p>
                    </div>
                </div>
                <button
                    onClick={() => props.onClose(false)}
                    className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    aria-label="Close menu"
                >
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
                {menuList.length > 0 ? (
                    menuList.map((menu) => {
                        const sub_menu_list = props.permissionData.filter(p => p.menu_id === menu.menu_id);
                        const isExpanded = expandedMenus.has(menu.menu_id);
                        const hasActiveItem = sub_menu_list.some(sub => 
                            isActive(location.pathname, sub.sub_menu_link)
                        );

                        return (
                            <div key={menu.menu_id} className="space-y-1">
                                <button
                                    onClick={() => toggleMenu(menu.menu_id)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            toggleMenu(menu.menu_id);
                                        }
                                    }}
                                    className={[
                                        "w-full flex items-center justify-between rounded-lg px-3 py-3 text-base font-semibold transition-colors touch-manipulation",
                                        hasActiveItem
                                            ? "bg-slate-800 text-white"
                                            : "text-slate-300 hover:bg-slate-800 hover:text-white",
                                    ].join(" ")}
                                    aria-expanded={isExpanded}
                                    aria-controls={`mobile-submenu-${menu.menu_id}`}
                                    aria-label={`Toggle ${menu.menu_name} menu`}
                                >
                                    <span className="uppercase tracking-wide">{menu.menu_name}</span>
                                    {isExpanded ? (
                                        <ChevronDownIcon className="h-5 w-5 transition-transform" aria-hidden="true" />
                                    ) : (
                                        <ChevronRightIcon className="h-5 w-5 transition-transform" aria-hidden="true" />
                                    )}
                                </button>

                                {isExpanded && (
                                    <ul 
                                        id={`mobile-submenu-${menu.menu_id}`}
                                        className="ml-2 space-y-1 border-l-2 border-slate-700 pl-2"
                                        role="menu"
                                        aria-label={`${menu.menu_name} submenu`}
                                    >
                                        {sub_menu_list.length > 0 &&
                                            sub_menu_list.map((sub, index) => {
                                                const active = isActive(location.pathname, sub.sub_menu_link);
                                                return (
                                                    <li key={`${sub.menu_id}-${index}`} role="none">
                                                        <Link
                                                            to={sub.sub_menu_link}
                                                            onClick={() => props.onClose(false)}
                                                            className={[
                                                                "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors touch-manipulation",
                                                                active
                                                                    ? "bg-indigo-600 text-white shadow-sm"
                                                                    : "text-slate-200 hover:bg-slate-800 hover:text-white",
                                                            ].join(" ")}
                                                            aria-current={active ? "page" : undefined}
                                                            role="menuitem"
                                                        >
                                                            <span className="ml-1">{sub.sub_menu_name}</span>
                                                        </Link>
                                                    </li>
                                                );
                                            })
                                        }
                                    </ul>
                                )}
                            </div>
                        );
                    })
                ) : (
                    <div className="space-y-2 px-3">
                        <div className="px-3 py-2">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                HR & Payroll
                            </p>
                        </div>
                        <ul className="space-y-1">
                            <li>
                                <Link
                                    to="/"
                                    onClick={() => props.onClose(false)}
                                    className={[
                                        "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors touch-manipulation",
                                        isActive(location.pathname, "/")
                                            ? "bg-indigo-600 text-white shadow-sm"
                                            : "text-slate-200 hover:bg-slate-800 hover:text-white",
                                    ].join(" ")}
                                >
                                    <span>Dashboard</span>
                                </Link>
                            </li>
                        </ul>
                    </div>
                )}
            </nav>

            {/* Footer */}
            <div className="border-t border-slate-800 px-6 py-4 text-sm text-slate-400">
                <p className="font-semibold text-white">Need help?</p>
                <p className="text-slate-400">Contact support or visit the help center.</p>
            </div>
        </div>
    );
}